import { MetaDataStatus } from 'data/model/app'
import { RootState } from 'data/reducer'
import {
  fetchLocalizationData,
  setBrowserMapHeaders,
  setBrowserMapPosition,
  setIsMobileDevice,
  setLanguage,
  setStatus,
  setUnit,
} from 'data/reducer/app'
import { setCountryCode } from 'data/reducer/user'
import { supportedLanguages } from 'izumi/supportedLanguages'
import { config, mapConfig, userConfig } from 'lib/config'
import { setTranslations, TranslationsDictionary } from 'lib/i18n'
import { createPlatform } from 'map/platform'
import { SearchResultEntityItem, SearchService } from 'map/services'
import { AnyAction } from 'redux'
import { Epic } from 'redux-observable'
import { Observable, of } from 'rxjs'
import { filter, switchMap } from 'rxjs/operators'
import { mapBrowserLanguageLocale } from 'utils/izumi'
import { logRefFromURL } from 'utils/router'
import { fetchUnit } from 'utils/storage'

type TranslationsResponse = {
  data: TranslationsDictionary
  options: {
    rtl: boolean
  }
}

type ResultPositionType = Pick<SearchResultEntityItem, 'position'>

const searchService = createPlatform().getSearchService() as unknown as SearchService

export const fetchLocalizationDataEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>
) =>
  action$.pipe(
    filter(fetchLocalizationData.match),
    switchMap(() => {
      const izumiLanguage = mapBrowserLanguageLocale(
        navigator.language,
        supportedLanguages,
        userConfig.language // default value
      )

      logRefFromURL(location.href)

      let countryCode = userConfig.defaultPoliticalView
      const browserMapHeaders = { city: 'Berlin', country: 'DE' }
      let isMobileDevice = false

      return new Observable<TranslationsDictionary>((observer) => {
        fetch(`${config.ROOT_PATH}translations/${izumiLanguage}.json`)
          .then((response) => {
            const xCountryHeader = response.headers.get('x-country')
            const xCityHeader = response.headers.get('x-city')
            const xIsMobileDeviceHeader = response.headers.get('x-is-mobile-device')

            if (xCountryHeader !== null) {
              countryCode = xCountryHeader
            }

            if (xCityHeader !== null && xCountryHeader !== null) {
              browserMapHeaders.city = xCityHeader
              browserMapHeaders.country = xCountryHeader
            }

            isMobileDevice = xIsMobileDeviceHeader === 'true'

            return response.json()
          })
          .then(({ data }: TranslationsResponse) => {
            observer.next(data)
            observer.complete()
          })
          .catch((error) => {
            observer.error(error)
          })
      }).pipe(
        switchMap((data) => {
          setTranslations(data)
          return of(
            setUnit(fetchUnit()),
            setLanguage(izumiLanguage),
            setIsMobileDevice(isMobileDevice),
            setCountryCode(countryCode),
            setBrowserMapHeaders({ browserMapHeaders })
          )
        })
      )
    })
  )

export const setBrowserMapHeadersEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>
) =>
  action$.pipe(
    filter(setBrowserMapHeaders.match),
    switchMap((action) => {
      return new Observable<ResultPositionType>((observer) => {
        searchService.geocode(
          {
            q: `${action.payload.browserMapHeaders.city}, ${action.payload.browserMapHeaders.country}`,
          },
          ({ items }) => {
            if (items !== undefined && items.length > 0 && items[0].position !== undefined) {
              observer.next({ position: items[0].position })
            } else {
              observer.next({ position: mapConfig.geolocation.defaultPosition })
            }

            observer.complete()
          },
          (error) => {
            observer.error(error)
          }
        )
      }).pipe(
        switchMap((data) =>
          of(setBrowserMapPosition(data.position), setStatus(MetaDataStatus.success))
        )
      )
    })
  )
